/**
 * src/gm/employees/prompt-builder.ts — prompt 组装（剧本 §2 的 JS 化，纪律全继承）。
 *
 * 纪律：
 *  - soul 放 system 消息且逐字引用，不改写一个字（简历即代码）；
 *  - 每次调用现读现拼 market 存档键，禁止模块级缓存——季度切换后 prompt 自动跟随；
 *  - 已拍板决策 JSON.stringify 原样注入，不摘要、不改写；
 *  - GDD §3 rubric 逐字引用（RUBRIC_TEXT，与 docs/gdd-lite.md 逐行对齐——
 *    gdd 改版时必须同步此处，这是手动同步点）。
 * 演出措辞边界：本模块只组装机制层模板；soul 人格文本归内容编剧，逐字注入不润色。
 */

import type { ChatMessage } from './api-client'
import type { Decision, Employee, MarketData, Project } from '../platform/save-types'

// ── GDD §3 rubric 逐字全文（评审 prompt 注入，禁止改写）─────────────────────
export const RUBRIC_TEXT = `## 3. 市场评分 rubric（评审 agent 对照打分用）

> **硬规则（先于一切锚点）：评审 agent 必须实际打开成品、完整游玩至少一局（从开始到结束/失败）后，方可打分。** 未游玩的评审结果无效，必须重评。

5 个维度，各 0-10 分，整数分。评审 agent **必须逐维对照锚点描述**打分，每维附一句话评语。以下给出 2 / 5 / 8 三档锚点；3/4/6/7/9 分由评审在相邻锚点间插值，10 分保留给"超出锚点想象的惊喜"。

### 3.0 事故锚点（0-1 分，全维度通用）

| 分档 | 锚点描述 |
|---|---|
| 0-1 | **事故级**：成品无法打开、白屏、或打开即报错导致完全无法进行游戏。此时 \`completion\` 必须为 0-1，其余维度基于可见部分给 0-2；评审必须在 \`overall_comment\` 中如实写明事故现象（这本身就是市场反馈的一部分） |

### 3.1 完成度 \`completion\`

| 分档 | 锚点描述 |
|---|---|
| 2 | **能打开但循环断裂**：存在阻断性 bug（玩不下去/无法结束）、明显占位内容（TODO 字样、纯色方块占位图）、或宣传的核心玩法根本没实现 |
| 5 | **核心循环完整**：从开始到结束（或到可玩目标）能跑通，但边界粗糙：偶发不阻断的 bug、结算/重开流程简陋、缺少必要的规则提示 |
| 8 | **完整闭环且有打磨**：开始→进行→结束→重开全流程顺滑；规则说明清晰；几乎无感 bug；且有超出预期的细节（暂停、计分板、难度递进至少占两项） |

### 3.2 创意 \`creativity\`

> **计价边界**：题材新颖度不计入本维度——"选了什么题材"由 \`theme_fit\` 计价；\`creativity\` 只评机制与表达层面的改动。

| 分档 | 锚点描述 |
|---|---|
| 2 | **纯模板复刻**：与该类型经典原作逐点一致，找不到任何自己的机制或表达 |
| 5 | **一处有效改动**：在经典框架上有且至少有 1 处明确可见的改动（新规则/新主题/新机制任选其一），改动成立但谈不上惊艳 |
| 8 | **有记忆点**：机制、叙事或表达上至少有一处让人想截图发给朋友的设计，且这个设计与玩法自洽，不是贴皮噱头 |

### 3.3 美术 \`art\`

| 分档 | 锚点描述 |
|---|---|
| 2 | **默认直出**：默认色块/系统字体/无样式按钮，无统一视觉方向，元素之间风格打架 |
| 5 | **有统一基调**：配色统一，主要元素经过绘制而非默认样式，但细节粗糙、几乎无动效 |
| 8 | **完整视觉风格**：调色板统一（全项目色值有明确清单，主色/辅色/点缀色分工可辨认）；有反馈性动效（命中特效、过渡动画、悬停反馈至少两项）；界面信息层级清晰；风格与题材互相加成 |

### 3.4 手感 \`game_feel\`

| 分档 | 锚点描述 |
|---|---|
| 2 | **操作劝退**：明显输入延迟或失灵；操作无反馈（按了像没按）；节奏拖沓或完全失控 |
| 5 | **响应正常**：操作即时生效，基本反馈齐全（得分、碰撞、胜负有提示），但没有"爽感"打磨，玩起来是"对的"而非"爽的" |
| 8 | **跟手且有层次**：操作精准跟手；反馈即时且有层次（音效、画面震动、粒子/闪光至少占两项）；节奏张弛有度；能让人产生"再来一局"的冲动 |

### 3.5 题材契合度 \`theme_fit\`

对照 \`data/market.json\` 当季 \`hot_themes\` / \`cold_themes\` / \`hot_mechanics\` 打分。

| 分档 | 锚点描述 |
|---|---|
| 2 | **完全错位**：撞上 cold_themes 且无任何反转处理，或干脆无视玩家 idea 的题材要求自说自话 |
| 5 | **不冷不热**：题材安全但平庸；或沾到了 hot 元素的边但没做透（挂了皮没入骨） |
| 8 | **精准踩点**：命中当季 hot_themes / hot_mechanics 至少一项并做出了特色，同时忠实回应了玩家 idea 的核心幻想 |`

// ── §2.1 市场认知摘要（剧本原文模板化，现读现拼）────────────────────────────
export function marketDigest(m: MarketData): string {
  const list = (arr: { name: string; note: string }[]) =>
    arr.map((x) => `${x.name}（${x.note}）`).join('；')
  return (
    `当季（${m.season}）市场风向：热门题材 ${list(m.hot_themes)}；` +
    `冷门雷区 ${list(m.cold_themes)}；热门机制 ${list(m.hot_mechanics)}。` +
    `编辑部点评：${m.editorial}`
  )
}

// ── 结构化输出 response_format ──────────────────────────────────────────────

const DECISION_ITEM_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: [
    'id',
    'project_id',
    'raised_by',
    'kind',
    'title',
    'description',
    'options',
    'player_choice',
    'status',
  ],
  properties: {
    id: { type: 'string', pattern: '^dec_[a-z0-9_]+$' },
    project_id: { type: 'string' },
    raised_by: { type: 'string' },
    kind: { type: 'string', enum: ['gameplay_variant', 'visual_direction'] },
    title: { type: 'string', minLength: 1 },
    description: { type: 'string', minLength: 1 },
    options: {
      type: 'array',
      minItems: 3,
      maxItems: 3,
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['key', 'style', 'label', 'description', 'expected_impact'],
        properties: {
          key: { type: 'string', enum: ['a', 'b', 'c'] },
          style: { type: 'string', enum: ['safe', 'bold', 'signature'] },
          label: { type: 'string', minLength: 1 },
          description: { type: 'string', minLength: 1 },
          expected_impact: { type: 'string', minLength: 1 },
        },
      },
    },
    player_choice: { type: 'null' },
    status: { type: 'string', enum: ['pending'] },
  },
}

/** 策划阶段结构化输出：决策数组包装 + design_doc 附带字段 */
export const PLAN_RESPONSE_FORMAT = {
  type: 'json_schema',
  json_schema: {
    name: 'tp3_plan',
    strict: true,
    schema: {
      type: 'object',
      additionalProperties: false,
      required: ['decisions', 'design_doc'],
      properties: {
        decisions: { type: 'array', minItems: 1, maxItems: 2, items: DECISION_ITEM_SCHEMA },
        design_doc: { type: 'string' },
      },
    },
  },
}

const DIMENSION_SCORE_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['score', 'comment'],
  properties: {
    score: { type: 'integer', minimum: 0, maximum: 10 },
    comment: { type: 'string', minLength: 1 },
  },
}

/** 评审分项子 schema（仅分项，五个数值字段是结算的活） */
export const REVIEW_RESPONSE_FORMAT = {
  type: 'json_schema',
  json_schema: {
    name: 'tp3_review',
    strict: true,
    schema: {
      type: 'object',
      additionalProperties: false,
      required: ['scores', 'market_fit_notes', 'overall_comment', 'playthrough_log', 'pen_name'],
      properties: {
        scores: {
          type: 'object',
          additionalProperties: false,
          required: ['completion', 'creativity', 'art', 'game_feel', 'theme_fit'],
          properties: {
            completion: DIMENSION_SCORE_SCHEMA,
            creativity: DIMENSION_SCORE_SCHEMA,
            art: DIMENSION_SCORE_SCHEMA,
            game_feel: DIMENSION_SCORE_SCHEMA,
            theme_fit: DIMENSION_SCORE_SCHEMA,
          },
        },
        market_fit_notes: { type: 'string', minLength: 1 },
        overall_comment: { type: 'string', minLength: 1 },
        playthrough_log: { type: 'string', minLength: 1 },
        pen_name: { type: 'string', minLength: 1 },
      },
    },
  },
}

// ── 各阶段 prompt 组装 ──────────────────────────────────────────────────────

/** §3 策划（六段式；注入市场摘要；不注入决策——尚无） */
export function buildPlanMessages(designer: Employee, project: Project, market: MarketData): ChatMessage[] {
  const shortId = project.id.replace(/^proj_/, '')
  return [
    { role: 'system', content: designer.soul },
    {
      role: 'user',
      content:
        `项目上下文：\n- 玩家 idea 原文：「${project.idea}」\n- 目标类型 target_genre：${project.target_genre}\n` +
        `- 项目 id：${project.id}\n- 你的员工 id：${designer.id}`,
    },
    { role: 'user', content: marketDigest(market) },
    {
      role: 'user',
      content:
        `交付物契约（决策生成，逐字要求）：\n` +
        `1. 先把玩家 idea 裁剪到 MVP 可开发规模：单个 HTML 文件、无后端、一局 ≤3 分钟能玩完。` +
        `idea 越界时（比如"做个开放世界"）由你砍到合理体量，裁剪理由写进决策 description——这是游戏机制，不是 bug。\n` +
        `2. 产出 1-2 个决策请求（默认 2 个：一个 gameplay_variant、一个 visual_direction；` +
        `玩家 idea 已明确指定某方向则跳过该方向，最少 1 个）。\n` +
        `3. 每个决策恰好 3 个选项，三个选项的 style 必须各为 safe / bold / signature 之一且互不相同。\n` +
        `4. 每个选项带 expected_impact（一句话，说清"选它成品会变成什么样"）。\n` +
        `5. id 命名约定 dec_${shortId}_01 / dec_${shortId}_02；raised_by 填 ${designer.id}；` +
        `status 一律 "pending"；player_choice 一律 null。\n` +
        `6. 同时把裁剪后的设计文档（核心循环一句话、规则清单、胜负/结束条件、操作方式）写进 design_doc 字段，要短，能看懂就行。`,
    },
    {
      role: 'user',
      content:
        `返回格式：严格按本次请求的 JSON Schema 返回结构化 JSON——` +
        `decisions 数组（1-2 个决策对象）+ design_doc 字符串。不要输出任何 schema 之外的解释文字。`,
    },
  ]
}

/** §4.1 美术（注入已拍板决策完整 JSON；不注入市场摘要——剧本 §2.1） */
export function buildArtMessages(artist: Employee, project: Project, resolvedDecisions: Decision[]): ChatMessage[] {
  return [
    { role: 'system', content: artist.soul },
    {
      role: 'user',
      content:
        `项目上下文：\n- 玩家 idea 原文：「${project.idea}」\n- 目标类型 target_genre：${project.target_genre}\n` +
        `- 项目 id：${project.id}`,
    },
    {
      role: 'user',
      content: `玩家已拍板的决策（完整 JSON，必须严格遵守）：\n${JSON.stringify(resolvedDecisions, null, 2)}`,
    },
    {
      role: 'user',
      content:
        `交付物契约（视觉规格，硬性清单）：\n` +
        `1. art-spec（视觉规格书）必须包含：\n` +
        `   - 色板清单：≤16 色，hex 值全列出，标注主色 / 辅色 / 点缀色 / 背景色的分工；\n` +
        `   - 字体栈与字号层级；\n` +
        `   - 关键元素样式规格：玩家角色 / 敌人或障碍物 / UI 控件 / 背景，各一段具体描述（尺寸、造型、状态变化）；\n` +
        `   - 动效规格：至少两项反馈性动效的具体参数（什么事件触发、持续多少 ms、什么表现形式）。\n` +
        `2. assets-css（可选）：提炼好的 CSS 变量与关键类，供程序直接引用；没有就留空段。`,
    },
    {
      role: 'user',
      content:
        `返回格式（分隔符协议，严格遵守，不要输出三段之外的任何文字）：\n` +
        `===ART_SPEC===\n（视觉规格书全文，Markdown）\n` +
        `===ASSETS_CSS===\n（CSS 变量与关键类；没有就留空）\n` +
        `===END===`,
    },
  ]
}

/** §4.2 程序（硬性注入美术产出全文 + 全部已拍板决策 + idea/genre + design_doc）
 *  lastFailure：事故情报（M5-B1）——再派一轮时把上一轮死因喂回，一次性消费 */
export function buildCodeMessages(
  programmer: Employee,
  project: Project,
  resolvedDecisions: Decision[],
  artSpec: string,
  assetsCss: string,
  designDoc: string | null,
  lastFailure: string | null = null,
): ChatMessage[] {
  const messages: ChatMessage[] = [
    { role: 'system', content: programmer.soul },
    {
      role: 'user',
      content: `项目上下文：\n- 玩家 idea 原文：「${project.idea}」\n- 目标类型 target_genre：${project.target_genre}`,
    },
    {
      role: 'user',
      content: `玩家已拍板的决策（完整 JSON，必须实现）：\n${JSON.stringify(resolvedDecisions, null, 2)}`,
    },
    {
      role: 'user',
      content:
        `美术视觉规格书全文（硬性要求先读再写，色板与动效规格不得违背；` +
        `如发现规格与玩法冲突，在交付说明注释里讲取舍，不得 silently 丢弃美术意图）：\n${artSpec}`,
    },
  ]
  if (assetsCss.trim()) {
    messages.push({ role: 'user', content: `美术提炼的 CSS 变量与关键类：\n${assetsCss}` })
  }
  if (designDoc?.trim()) {
    messages.push({ role: 'user', content: `策划设计文档：\n${designDoc}` })
  }
  if (lastFailure?.trim()) {
    messages.push({
      role: 'user',
      content: `上一版交付未通过验收，死因如下——逐项修复后重新交付完整 index.html：\n${lastFailure}`,
    })
  }
  messages.push({
    role: 'user',
    content:
      `交付契约：产出一个单文件、零依赖、开箱即玩的 HTML5 游戏 index.html——` +
      `所有 CSS/JS 内联；禁止任何外部 CDN / 外链资源（字体 CDN 也禁）；禁止占位符与 TODO；` +
      `游戏运行在严格沙箱中：禁止 localStorage / sessionStorage / IndexedDB / cookie 等一切持久化 API（try/catch 包裹也禁止），需要记忆的状态一律用内存变量；` +
      `开始→进行→结束/失败→重开核心循环完整，键盘/鼠标操作即时生效。\n` +
      `返回格式：直接输出完整 HTML 全文（可用 \`\`\`html 围栏包裹），不要输出任何解释文字。`,
  })
  return messages
}

/** §5 评审（独立"市场评审员"人设；rubric 逐字 + 市场 + 成品全文 + 冒烟报告）
 *  penName 为 null 表示本季度首次评审：要求模型在 pen_name 字段自选笔名（同季度同一支笔） */
export function buildReviewMessages(
  penName: string | null,
  project: Project,
  market: MarketData,
  resolvedDecisions: Decision[],
  indexHtml: string,
  smokeReportJson: string,
): ChatMessage[] {
  const identity = penName
    ? `你是「${penName}」，一位毒舌但有底线的老牌游戏媒体人，本季度为《掌上街机》杂志审阅小厂作品无数。` +
      `返回 JSON 的 pen_name 字段填你现在的笔名「${penName}」。`
    : `你是一位毒舌但有底线的老牌游戏媒体人，本季度为《掌上街机》杂志审阅小厂作品无数。` +
      `这是你本季度第一次执笔——请在返回 JSON 的 pen_name 字段自选一个老牌媒体人笔名（2-6 个字，有年代感，此后本季度固定用它署名）。`
  return [
    {
      role: 'system',
      content:
        identity +
        `你的评论有性格：毒舌或暖心皆可，但绝不写平庸套话。打分严格对照 rubric 锚点，讲证据不讲情面。`,
    },
    { role: 'user', content: `评分 rubric（逐字对照打分）：\n\n${RUBRIC_TEXT}` },
    {
      role: 'user',
      content:
        `${marketDigest(market)}\n\n五维权重 dimension_weights：${JSON.stringify(market.dimension_weights)}`,
    },
    {
      role: 'user',
      content:
        `项目背景：\n- 玩家 idea 原文：「${project.idea}」\n- 目标类型：${project.target_genre}\n` +
        `- 玩家已拍板的决策：\n${JSON.stringify(resolvedDecisions, null, 2)}`,
    },
    { role: 'user', content: `成品 index.html 全部源码：\n${indexHtml}` },
    { role: 'user', content: `沙箱自动冒烟报告（机器试玩证据，JSON）：\n${smokeReportJson}` },
    {
      role: 'user',
      content:
        `游玩协议（浏览器降级版，必须依次完成）：\n` +
        `1. 通读上面注入的 index.html 全部源码，定位核心循环代码并在评语中引用关键片段；\n` +
        `2. 从初始状态逐步推演至少一局完整流程（含失败/结束与重开路径），推演要点写入 playthrough_log 字段；\n` +
        `3. 结合冒烟报告结论：verdict 为 FAIL 时 completion 锚定 0-1 事故级；` +
        `PASS 但推演发现阻断 bug 时 completion ≤2；\n` +
        `4. 在 overall_comment 或 market_fit_notes 中如实标注"本轮为沙箱冒烟 + 代码级试玩"。\n` +
        `返回格式：严格按本次请求的 JSON Schema 返回——scores 五维分项（整数 0-10 + 一句话评语）、` +
        `market_fit_notes、overall_comment（2-4 句，有人格）、playthrough_log。` +
        `只返回分项部分；总分与盈亏由编辑部统一核算，不用你算。`,
    },
  ]
}
